import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "../components/PasswordInput";
import { authApi, type InvitationInfo } from "../api/auth";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { authFallbackError } from "../lib/authErrors";

type TFn = (key: string, fallback: string) => string;

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const { refresh } = useAuth();
  const token = searchParams.get("token") ?? "";

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError({ status: 400, code: "MISSING_TOKEN", message: "No token in URL" });
      return;
    }
    authApi
      .inspectInvitation(token)
      .then((r) => setInfo(r))
      .catch((e: ApiError) => setLoadError(e));
  }, [token]);

  if (loadError) {
    return (
      <CenteredCard>
        <h1 style={titleStyle}>{t("accept.unavailable", "Pozivnica nije dostupna")}</h1>
        <p>
          {loadError.code === "INVITATION_EXPIRED"
            ? t("errors.invitationExpired", "Ova pozivnica je istekla. Obratite se osobi koja vas je pozvala za novi link.")
            : loadError.code === "INVITATION_USED"
              ? t("errors.invitationUsed", "Ova pozivnica je već prihvaćena. Pokušajte da se prijavite.")
              : t("errors.invitationGeneric", "Pozivnica nije mogla da se otvori. Proverite link.")}
        </p>
        <Link to="/login" style={linkStyle}>
          {t("accept.goToSignIn", "Idi na prijavu")}
        </Link>
      </CenteredCard>
    );
  }

  if (!info) {
    return (
      <CenteredCard>
        <p>{t("accept.loading", "Učitavanje pozivnice…")}</p>
      </CenteredCard>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t("errors.nameRequired", "Unesite ime i prezime."));
      return;
    }
    if (!Object.values(checkPasswordClient(password)).every(Boolean)) {
      setSubmitError(t("errors.passwordPolicy", "Lozinka ne ispunjava zahteve."));
      return;
    }
    if (password !== confirm) {
      setSubmitError(t("errors.passwordsMismatch", "Lozinke se ne poklapaju."));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.acceptInvitation(token, {
        firstName,
        lastName,
        password,
        phone: phone.trim() || undefined,
      });
      await refresh();
      // Land on the profile with the welcome flag so the user is prompted to
      // upload an avatar before heading into the app proper.
      navigate("/profil?welcome=1", { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setSubmitError(translatePasswordError(apiErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  const passwordRules = checkPasswordClient(password);

  return (
    <CenteredCard>
      <h1 style={titleStyle}>{t("accept.welcome", "Dobrodošli u Bankom CMMS")}</h1>
      <p style={{ color: "var(--ink-3)", fontSize: 14 }}>
        {t("accept.joiningAs", "Pridružujete se kao")} <strong>{info.email}</strong>
        {info.roles.length > 0 ? <> — {t("accept.role", "uloga")}: {info.roles.map((r) => r.name).join(", ")}</> : null}.
      </p>

      {submitError && <div style={errorStyle} role="alert">{submitError}</div>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }} noValidate>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>{t("accept.firstName", "Ime")}</span>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              maxLength={100}
              required
              style={inputStyle}
            />
          </label>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>{t("accept.lastName", "Prezime")}</span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              maxLength={100}
              required
              style={inputStyle}
            />
          </label>
        </div>

        <label style={labelStyle}>
          <span>{t("accept.phone", "Telefon")} <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>({t("accept.optional", "opciono")})</span></span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+381 11 123 4567"
            autoComplete="tel"
            maxLength={40}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>{t("accept.password", "Lozinka")}</span>
          <PasswordInput
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            inputStyle={inputStyle}
          />
          <ul style={hintListStyle}>
            <Rule ok={passwordRules.minLength}>{t("reset.policy.length", "Najmanje 12 znakova")}</Rule>
            <Rule ok={passwordRules.hasUpper}>{t("reset.policy.upper", "Sadrži veliko slovo")}</Rule>
            <Rule ok={passwordRules.hasLower}>{t("reset.policy.lower", "Sadrži malo slovo")}</Rule>
            <Rule ok={passwordRules.hasDigit}>{t("reset.policy.digit", "Sadrži cifru")}</Rule>
            <li style={{ color: "var(--ink-3)" }}>
              {t("reset.policy.breach", "Ne sme biti poznata ukradena lozinka (proveravamo javnu listu).")}
            </li>
          </ul>
        </label>
        <label style={labelStyle}>
          <span>{t("accept.confirmPassword", "Potvrdite lozinku")}</span>
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            required
            inputStyle={inputStyle}
          />
        </label>

        <button type="submit" disabled={submitting} style={btnStyle}>
          {submitting ? t("accept.submitting", "Kreiranje naloga…") : t("accept.submit", "Kreiraj nalog")}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return <div style={shellStyle}><div style={cardStyle}>{children}</div></div>;
}

function Rule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li style={{ color: ok ? "var(--ok)" : "var(--ink-3)", listStyle: "none", display: "flex", gap: 6 }}>
      <span aria-hidden>{ok ? "✓" : "○"}</span>
      <span>{children}</span>
    </li>
  );
}

function checkPasswordClient(p: string) {
  return {
    minLength: p.length >= 12,
    hasUpper: /[A-Z]/.test(p),
    hasLower: /[a-z]/.test(p),
    hasDigit: /\d/.test(p),
  };
}

function translatePasswordError(err: ApiError, t: TFn): string {
  switch (err.code) {
    case "PASSWORD_TOO_SHORT":
    case "PASSWORD_NEEDS_UPPERCASE":
    case "PASSWORD_NEEDS_LOWERCASE":
    case "PASSWORD_NEEDS_DIGIT":
      return t("errors.passwordPolicy", "Lozinka ne ispunjava zahteve.");
    case "PASSWORD_BREACHED":
      return t("errors.passwordBreached", "Ova lozinka se pojavljuje na poznatim listama ukradenih lozinki. Izaberite drugu.");
    case "INVITATION_EXPIRED":
      return t("errors.invitationExpired", "Ova pozivnica je istekla. Obratite se osobi koja vas je pozvala za novi link.");
    case "INVITATION_USED":
      return t("errors.invitationUsed", "Ova pozivnica je već prihvaćena. Pokušajte da se prijavite.");
    case "INVITATION_INVALID":
      return t("errors.invitationGeneric", "Pozivnica nije mogla da se otvori. Proverite link.");
    case "VALIDATION":
      return t("errors.acceptValidation", "Proverite ime, prezime i telefon.");
    default:
      return authFallbackError(err, t, t("errors.acceptGeneric", "Pozivnica nije mogla da se prihvati."));
  }
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg)",
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 480,
  background: "var(--surface)",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const titleStyle: React.CSSProperties = { margin: 0, color: "var(--brand-primary)", fontSize: 24 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid var(--border-strong)",
  // Explicit so the dark theme does not keep the browser's white field with light text.
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: 15,
};
const btnStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "var(--brand-primary)",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = { color: "var(--brand-primary)", fontSize: 14 };
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "rgba(182,53,58,.12)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 14,
};
const hintListStyle: React.CSSProperties = {
  margin: "8px 0 0",
  padding: 0,
  fontSize: 12,
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
